import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionContext";
import {
  Users, TrendingUp, AlertTriangle, AlertOctagon, ClipboardList,
  Megaphone, Bell, CalendarDays, CheckCircle2, Activity,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { tr, LESSON_STATUS_LABEL, LESSON_TYPE_LABEL, fmtDateBR } from "@/lib/labels";
import {
  EmptyState,
  PageHeader,
  PageLoading,
  SectionCard,
  SectionLink,
  StatCard,
} from "@/components/doxa/AdminSurface";

const PENDING_ITEMS = [
  { key: "pending_registrations", label: "Inscrições pendentes", to: "/registrations", testid: "pend-registrations" },
  { key: "critical_students", label: "Alunos críticos", to: "/students?classification=CRITICAL", testid: "pend-critical" },
  { key: "recent_justifications", label: "Justificativas registradas", to: null, testid: "pend-justifications" },
  { key: "overdue_tasks", label: "Tarefas vencidas", to: "/tasks", testid: "pend-overdue-tasks" },
  { key: "open_lessons", label: "Aulas abertas", to: "/lessons", testid: "pend-open-lessons" },
  { key: "upcoming_lessons", label: "Aulas agendadas próximas", to: "/lessons", testid: "pend-upcoming-lessons" },
  { key: "students_no_access", label: "Alunos sem acesso ativo", to: "/students", testid: "pend-no-access" },
  { key: "active_classes_no_content", label: "Turmas ativas sem conteúdo", to: "/classes", testid: "pend-empty-classes" },
  { key: "physical_materials_pending", label: "Materiais físicos pendentes", to: "/materials", testid: "pend-physical-materials" },
  { key: "students_without_notifications", label: "Alunos sem notificações", to: "/notifications", testid: "pend-without-notifications" },
];

function todayLabel() {
  const value = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  }).format(new Date());
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function compactLessonLabel(row) {
  if (!row) return "";
  const date = row.lesson_date ? fmtDateBR(row.lesson_date) : "Data a definir";
  return row.start_time ? `${date} · ${row.start_time}` : date;
}

function AttendanceTrendCard({ analytics, canViewReports, average }) {
  const trend = (analytics?.lesson_trend || [])
    .filter((row) => row.percent != null)
    .slice(-8)
    .map((row, index) => ({
      ...row,
      shortLabel: row.lesson_date ? fmtDateBR(row.lesson_date).slice(0, 5) : `A${index + 1}`,
    }));

  return (
    <section className="rounded-[16px] border border-[#E0D9CD] bg-white p-4 sm:p-5" data-testid="attendance-trend-card">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-bold">Presença nas últimas aulas</h2>
          <p className="mt-0.5 text-xs text-[#6B655E]">
            {analytics?.class_group?.name || "Evolução recente da frequência"}
          </p>
        </div>
        {canViewReports && <SectionLink to="/reports">Relatórios</SectionLink>}
      </div>

      {trend.length >= 2 ? (
        <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
          <div className="h-[210px] min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 12, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="doxaAttendanceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F75A1B" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#F75A1B" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EEE7DD" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="shortLabel" tick={{ fontSize: 10, fill: "#8A8378" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 10, fill: "#8A8378" }} axisLine={false} tickLine={false} />
                <ChartTooltip
                  contentStyle={{ border: "1px solid #E0D9CD", borderRadius: 12, fontSize: 12, boxShadow: "0 10px 28px rgba(68,53,35,.08)" }}
                  formatter={(value) => [`${value}%`, "Presença"]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.title || "Aula"}
                />
                <Area type="monotone" dataKey="percent" stroke="#F75A1B" strokeWidth={2.5} fill="url(#doxaAttendanceFill)" activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-2 border-t border-[#EBE5DB] pt-3 sm:block sm:min-w-[88px] sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
            <p className="font-display text-3xl font-bold">{average == null ? "—" : `${average}%`}</p>
            <p className="text-[11px] leading-tight text-[#8A8378] sm:mt-1">média<br className="hidden sm:block" /> geral</p>
          </div>
        </div>
      ) : (
        <div className="flex min-h-[210px] items-center justify-center rounded-xl bg-[#FAF7F1] px-5 text-center">
          <div>
            <TrendingUp className="mx-auto h-7 w-7 text-[#F75A1B]" />
            <p className="mt-2 text-sm font-semibold text-[#1F1F1F]">{canViewReports ? "Histórico insuficiente para desenhar a tendência" : "Tendência disponível para perfis com acesso a Relatórios"}</p>
            <p className="mt-1 text-xs text-[#6B655E]">A média geral continua visível nos indicadores acima.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function NextLessonHero({ lesson, remainingCount }) {
  if (!lesson) {
    return (
      <section className="rounded-[16px] border border-[#E0D9CD] bg-white p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Próxima aula</h2>
          <SectionLink to="/lessons">Ver aulas</SectionLink>
        </div>
        <EmptyState icon={CalendarDays} title="Nenhuma aula agendada" description="Quando uma nova aula for programada, ela aparecerá aqui." compact />
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-[16px] border border-[#E0D9CD] bg-white" data-testid="dashboard-next-lesson">
      <div className="flex items-center justify-between px-4 pt-4 sm:px-5 sm:pt-5">
        <h2 className="font-display text-lg font-bold">Próxima aula</h2>
        <SectionLink to="/lessons">Ver aulas</SectionLink>
      </div>
      <Link to={`/lessons/${lesson.id}`} className="group relative mt-3 block min-h-[238px] overflow-hidden bg-[#2A211D] p-5 text-white sm:p-6">
        <div className="absolute inset-0 bg-[linear-gradient(125deg,#2A211D_5%,#6E3C27_48%,#F75A1B_120%)]" />
        <div className="absolute -bottom-20 -right-14 h-56 w-72 rotate-[-10deg] rounded-[45%] bg-[#F75A1B]/70 blur-[1px]" />
        <div className="absolute -bottom-24 right-28 h-52 w-64 rotate-[8deg] rounded-[50%] bg-[#D94A12]/55" />
        <div className="absolute bottom-[-74px] right-[-10px] h-44 w-80 rotate-[16deg] rounded-[50%] border-[18px] border-[#FFB38D]/30" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(24,18,15,.92)_0%,rgba(24,18,15,.62)_48%,rgba(24,18,15,.12)_100%)]" />

        <div className="relative z-10 flex min-h-[190px] max-w-[82%] flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-white/80">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#F75A1B] ring-4 ring-white/10" />{compactLessonLabel(lesson)}</span>
              <span>·</span>
              <span>{tr(LESSON_TYPE_LABEL, lesson.lesson_type)}</span>
            </div>
            <h3 className="mt-4 font-display text-2xl font-bold leading-tight text-white sm:text-3xl">{lesson.title}</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`doxa-badge ${lesson.status === "OPEN" ? "doxa-badge-success" : "bg-white/15 text-white"}`}>{tr(LESSON_STATUS_LABEL, lesson.status)}</span>
            {remainingCount > 0 && <span className="text-xs text-white/70">+ {remainingCount} aula(s) na sequência</span>}
          </div>
        </div>

        <span className="absolute bottom-5 right-5 z-10 grid h-11 w-11 place-items-center rounded-full bg-white text-[#F75A1B] shadow-xl transition-transform group-hover:translate-x-1">→</span>
      </Link>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [stats, setStats] = useState(null);
  const [pend, setPend] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const nav = useNavigate();

  const canViewReports = user?.role === "MASTER" || can("view.reports");

  const load = useCallback(async () => {
    const [statsResponse, pendResponse] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/pendencias").catch(() => null),
    ]);

    const nextStats = statsResponse.data;
    setStats(nextStats);
    setPend(pendResponse?.data || null);

    const classGroupId = (nextStats.upcoming_lessons || [])[0]?.class_group_id;
    if (canViewReports && classGroupId) {
      api.get(`/analytics/classes/${classGroupId}`)
        .then(({ data }) => setAnalytics(data))
        .catch(() => setAnalytics(null));
    } else {
      setAnalytics(null);
    }
  }, [canViewReports]);

  useEffect(() => { void load().catch(() => {}); }, [load]);
  useVisibleRefresh(() => load(), { intervalMs: 0 });

  if (!stats) return <PageLoading label="Carregando visão geral…" />;

  const firstName = user?.name?.trim()?.split(/\s+/)?.[0] || "equipe";
  const upcomingLessons = stats.upcoming_lessons || [];
  const nextLesson = upcomingLessons[0] || null;
  const pendingTotal = pend ? PENDING_ITEMS.reduce((sum, item) => sum + (Number(pend[item.key]) || 0), 0) : 0;
  const enrolledStudents = (Number(stats.active_students) || 0) + (Number(stats.inactive_students) || 0);
  const hasAttendance = (stats.last_checkins || []).length > 0;

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="dashboard-page">
      <PageHeader
        compact
        eyebrow="Visão operacional"
        title={`Olá, ${firstName}! 👋`}
        description="Sua escola está em movimento. Veja primeiro o resumo de hoje."
        meta={todayLabel()}
      />

      <section aria-labelledby="dashboard-priorities-title">
        <div className="mb-2.5 flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Agora</p>
            <h2 id="dashboard-priorities-title" className="font-display text-lg font-bold">Indicadores principais</h2>
          </div>
          <span className="hidden text-[11px] text-[#8A8378] sm:block">Atualiza quando você retorna à tela</span>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            compact
            title="Alunos matriculados"
            value={enrolledStudents}
            hint="Ativos + inativos"
            icon={Users}
            tone="orange"
            testId="kpi-enrolled-students"
          />
          <StatCard
            compact
            title="Alunos ativos"
            value={stats.active_students}
            hint="Base acadêmica ativa"
            icon={CheckCircle2}
            tone="success"
            testId="kpi-active-students"
          />
          <StatCard
            compact
            title="Presença média"
            value={hasAttendance ? `${stats.avg_attendance}%` : "Sem dados"}
            hint="Frequência consolidada"
            icon={TrendingUp}
            tone="neutral"
            testId="kpi-avg-attendance"
          />
          <StatCard
            compact
            showActionLabel={false}
            title="Em risco de faltas"
            value={stats.critical}
            hint="Prioridade de acompanhamento"
            icon={AlertOctagon}
            tone="critical"
            testId="kpi-critical"
            onClick={() => nav("/students?classification=CRITICAL")}
          />
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[1.12fr_.88fr]">
        <AttendanceTrendCard analytics={analytics} canViewReports={canViewReports} average={hasAttendance ? stats.avg_attendance : null} />
        <NextLessonHero lesson={nextLesson} remainingCount={Math.max(upcomingLessons.length - 1, 0)} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.72fr_1.28fr]">
        <SectionCard
          compact
          title="Saúde da operação"
          description="Contexto complementar para acompanhamento da escola."
          icon={Activity}
          testId="dashboard-health-card"
        >
          <div className="divide-y divide-[#EBE5DB]">
            <div className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
              <div><p className="text-sm font-medium">Em dia</p><p className="text-[11px] text-[#8A8378]">Faixa saudável</p></div>
              <span className="inline-flex items-center gap-1.5 font-display text-xl font-bold"><CheckCircle2 className="h-4 w-4 text-[#2F855A]" />{stats.on_track}</span>
            </div>
            <button type="button" onClick={() => nav("/students?classification=ATTENTION")} className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:text-[#D94A12]">
              <div><p className="text-sm font-medium">Em atenção</p><p className="text-[11px] text-[#8A8378]">Acompanhamento preventivo</p></div>
              <span className="inline-flex items-center gap-1.5 font-display text-xl font-bold"><AlertTriangle className="h-4 w-4 text-[#C05621]" />{stats.attention}</span>
            </button>
            <div className="flex items-center justify-between gap-3 py-2.5">
              <div><p className="text-sm font-medium">Tarefas ativas</p><p className="text-[11px] text-[#8A8378]">Conteúdos em andamento</p></div>
              <span className="inline-flex items-center gap-1.5 font-display text-xl font-bold"><ClipboardList className="h-4 w-4 text-[#4A4640]" />{stats.active_tasks}</span>
            </div>
            <div className="flex items-center justify-between gap-3 py-2.5 last:pb-0">
              <div><p className="text-sm font-medium">Avisos ativos</p><p className="text-[11px] text-[#8A8378]">Comunicações disponíveis</p></div>
              <span className="inline-flex items-center gap-1.5 font-display text-xl font-bold"><Megaphone className="h-4 w-4 text-[#F75A1B]" />{stats.active_notices}</span>
            </div>
          </div>
        </SectionCard>

        {pend && (
          <SectionCard
            compact
            title="Pendências"
            description={pendingTotal > 0 ? `${pendingTotal} ocorrência(s) para revisar no painel.` : "Nenhuma pendência operacional relevante neste momento."}
            icon={Bell}
            testId="pendencias-section"
          >
            <div className="grid gap-x-5 md:grid-cols-2">
              {PENDING_ITEMS.map((item) => {
                const value = Number(pend[item.key]) || 0;
                return (
                  <div key={item.key} className="flex min-h-[50px] items-center gap-2.5 border-b border-[#EBE5DB] py-2.5 last:border-b-0 md:[&:nth-last-child(-n+2)]:border-b-0" data-testid={item.testid}>
                    <span className={`grid h-8 min-w-8 shrink-0 place-items-center rounded-lg px-1.5 font-display text-xs font-bold ${value > 0 ? "bg-[#FFF0E9] text-[#D94A12]" : "bg-[#F6F3EE] text-[#8A8378]"}`}>{value}</span>
                    <p className={`min-w-0 flex-1 text-xs ${value > 0 ? "font-semibold text-[#1F1F1F]" : "text-[#6B655E]"}`}>{item.label}</p>
                    {item.to && value > 0 && (
                      <button type="button" onClick={() => nav(item.to)} className="shrink-0 rounded-md px-1.5 py-1 text-[11px] font-semibold text-[#F75A1B] hover:bg-[#FFF0E9]" data-testid={`${item.testid}-ver`}>Ver</button>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
